import { Navigate } from 'react-router'
import { useSession } from '../session'
import { useNow } from '../hooks'
import { t } from '../i18n'
import type { MePlan, User } from '../api'
import { Banner, Chip, Icon } from '../ui'
import { withMono } from '../mono'
import { hideNoMarkRow, outputLimit } from '../live-ui/watermark'
import {
  FEATURE_ROWS, LIMIT_ROWS, accessText, badgeClass, barLabel, hasFeature, hasPlan, limitText, longDate, num, planEnded, pricingUrl, statusLine,
  type LimitValue,
} from '../billing'

/*
 * Account → Plan & usage (BILL-B5a; docs/billing/design.md §10.1) on the Strevix system
 * (PlansAndLimits, UsageMeters, StateSystem): the plan and its status, what it allows and what
 * is not on it. No payments yet (owner decision Q10): "Choose a plan" opens the site's pricing
 * page; no Manage billing, no invoices. Usage bars come with the usage counting (BILL-B3:
 * `usage` in GET /api/me). The box (plan: null) has no such page: back to the Live screen.
 */

/** What the plan's state means for the streamer, below the status line. */
function note(u: User & { plan: MePlan }): string | undefined {
  const p = u.plan
  const l = u.limits ?? {}
  if (p.status === 'trialing' && p.id === 'trial' && p.trial_ends_at) return t('plan.trialNote', { date: longDate(p.trial_ends_at) })
  if (p.id === 'ambassador') return t('plan.ambassadorNote')
  if (p.id === 'legacy') return p.until ? t('plan.legacyNote', { date: longDate(p.until) }) : undefined
  if (p.source === 'grant') return p.until ? t('plan.giftNote', { date: longDate(p.until) }) : t('plan.giftForever')
  if (planEnded(p)) return t('plan.endedNote')
  if (p.status === 'past_due') return t('plan.paymentNote')
  if (p.id === 'free') {
    const v = (k: string, d: number) => (typeof l[k] === 'number' ? num(l[k] as number) : num(d))
    return t('plan.freeNote', { streams: v('concurrent_streams', 1), dests: v('destinations', 2), hours: v('stream_hours_month', 20) })
  }
  return undefined
}

/** The limit's second line: why it behaves as it does at the limit (design §4.1, Q11, Q12). */
function limitSub(key: string, p: MePlan): string | undefined {
  if (key === 'ingest_max') return t('plan.ingestWarn')
  if (key === 'stream_hours_month') return p.id === 'trial' || p.id === 'free' ? t('plan.hoursHard') : t('plan.hoursFair')
  return undefined
}

// DS: PL-07 — PlanFeatures (.sx-limits / .sx-feats) after the system import; nearest classes until then
function Limits({ u }: { u: User & { plan: MePlan } }) {
  const l: Record<string, LimitValue> = u.limits ?? {}
  const rows: { key: string; label: (typeof LIMIT_ROWS)[number]['label']; text?: string; sub?: string }[] = []
  // WM3 (WMU-06): the free plan's output, right after the input — only for a plan with the Strevix mark, once the flag is on
  const out = outputLimit(u.watermark)
  for (const r of LIMIT_ROWS) {
    const text = limitText(r.unit, l[r.key])
    if (text !== undefined) rows.push({ key: r.key, label: r.label, text, sub: limitSub(r.key, u.plan) })
    if (r.key === 'ingest_max' && out) rows.push({ key: 'output_max', label: 'plan.limit.output_max', text: out, sub: t('plan.outputMarkNote') })
  }
  return (
    <section className="sx-panel flex flex-col gap-3" aria-labelledby="plan-limits">
      <h2 id="plan-limits" className="sx-label m-0">{t('plan.limits')}</h2>
      <dl className="m-0 flex flex-col">
        {rows.map((r) => {
          const sub = r.sub
          const off = l[r.key] === 0
          return (
            <div key={r.key} data-testid={`plan-limit-${r.key}`} className="flex flex-col gap-1 border-b border-line py-2.5 last:border-b-0 sm:flex-row sm:items-baseline sm:justify-between sm:gap-6">
              <dt className="flex min-w-0 flex-col gap-0.5 text-ui-body-sm text-text">
                {t(r.label)}
                {sub && <span className="text-ui-caption text-muted">{sub}</span>}
              </dt>
              <dd className={`tabular m-0 whitespace-nowrap font-mono text-ui-body-sm sm:text-right ${off ? 'text-muted' : 'text-text'}`}>{r.text}</dd>
            </div>
          )
        })}
      </dl>
    </section>
  )
}

function Features({ u }: { u: User & { plan: MePlan } }) {
  const l: Record<string, LimitValue> = u.limits ?? {}
  return (
    <section className="sx-panel flex flex-col gap-3" aria-labelledby="plan-features">
      <h2 id="plan-features" className="sx-label m-0">{t('plan.features')}</h2>
      <ul className="m-0 flex list-none flex-col p-0">
        {/* WM3 (WMU-06): while the flag is off nobody has the mark, so a plan with it does not list "No Strevix mark" */}
        {FEATURE_ROWS.filter((f) => f.id !== 'no_watermark' || !hideNoMarkRow(u.watermark)).map((f) => {
          const on = hasFeature(u.plan, l, f)
          const soon = !on && !!f.soon?.(l)
          return (
            <li key={f.id} data-testid={`plan-feature-${f.id}`} data-on={on} data-soon={soon || undefined} className="flex items-center justify-between gap-4 border-b border-line py-2.5 text-ui-body-sm last:border-b-0">
              <span className={on ? 'text-text' : 'text-muted'}>{t(f.label)}</span>
              {/* the word carries it; the icon only repeats it (state is never colour alone) */}
              <span className={`inline-flex items-center gap-1.5 whitespace-nowrap text-ui-caption ${on ? 'text-text-soft' : 'text-muted'}`}>
                {!soon && <Icon name={on ? 'check' : 'close'} size={12} />}
                {on ? t('plan.included') : soon ? t('plan.comingSoon') : t('plan.notOnPlan')}
              </span>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

export function BillingPage() {
  const { user, loading } = useSession()
  const now = useNow(60_000)
  if (loading) return <p className="text-sm text-muted">{t('plan.loading')}</p>
  // the box (BILLING_MODE=off): no plans
  if (!hasPlan(user)) return <Navigate to="/" replace />
  const p = user.plan
  const bar = barLabel(user, now)
  const blocked = accessText(p)
  const sub = note(user)
  return (
    <div data-testid="billing-page" className="flex max-w-3xl flex-col gap-5">
      <div className="border-b border-line pb-5">
        {/* account pages: title in ui-heading (04 product scale; PL-06, Devices and API tokens too) */}
        <h1 className="m-0 text-ui-heading text-text">{t('plan.title')}</h1>
        <p className="mt-2 mb-0 text-ui-body text-text-soft">{t('plan.intro')}</p>
      </div>

      {blocked && (
        <Banner state="fail">
          <span data-testid="plan-access">{withMono(blocked)}</span>
        </Banner>
      )}

      <section className="sx-panel flex flex-col gap-4" aria-labelledby="plan-now">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-2">
            <span className="sx-label">{t('plan.label')}</span>
            <div className="flex flex-wrap items-center gap-2.5">
              <span data-testid="plan-badge" className={`sx-plan ${badgeClass(p)}`}>{p.name}</span>
              {user.badges?.includes('vip') && <span data-testid="plan-vip" className="sx-plan business">VIP</span>}
              {bar && bar.tone !== 'idle' && <Chip state={bar.tone} size="sm">{bar.text}</Chip>}
            </div>
            <h2 id="plan-now" data-testid="plan-status" className="m-0 text-ui-heading text-text">{withMono(statusLine(user, now))}</h2>
          </div>
          <div className="flex flex-col items-start gap-1.5 sm:items-end">
            {/* DS: PL-03 — a link styled as the view's one primary button: it leaves for the site */}
            <a href={pricingUrl()} target="_blank" rel="noopener noreferrer" data-testid="plan-choose"
              className={`sx-btn ${p.id === 'ambassador' ? '' : 'primary'} no-underline max-md:min-h-[44px]`}>
              {t('plan.choose')}
              <Icon name="external" size={16} />
            </a>
            <span className="sx-cap max-w-64 sm:text-right">{t('plan.chooseHint')}</span>
          </div>
        </div>
        {sub && <p data-testid="plan-note" className="m-0 text-ui-body text-text-soft">{withMono(sub)}</p>}
      </section>

      <Limits u={user} />
      <Features u={user} />
      {/* DS: PL-05 — usage bars (UsageMeters, Quota) come with BILL-B3 */}
      <p data-testid="plan-usage-later" className="sx-cap m-0">{t('plan.usageLater')}</p>
    </div>
  )
}
